import type { AppLocale } from "./locale";
import type { Product } from "./types";

export type RequiredCard = "casa_mega_digital";

export function requiredCardText(card: RequiredCard | null | undefined, locale: AppLocale): string {
  if (card !== "casa_mega_digital") return "";
  return {
    ru: "С цифровой картой Casa Mega",
    ro: "Cu cardul digital Casa Mega",
    uk: "З цифровою карткою Casa Mega",
  }[locale];
}

/** Presentation only. Never use this fallback for totals, comparisons or alerts. */
export function productDisplayPrice(product: Product): {
  current: number;
  old: number | null;
  discount: number | null;
  conditional: boolean;
} | null {
  if (typeof product.new_price_mdl === "number" && Number.isFinite(product.new_price_mdl)) {
    return { current: product.new_price_mdl, old: product.old_price_mdl,
      discount: product.discount_percent, conditional: false };
  }
  const notice = product.offer_notice;
  if (notice?.policy !== "retain-with-catalog-notice-v1"
      || notice.kind !== "conditions" || notice.status_as_of !== "active"
      || notice.price_verified !== true || notice.identity_verified !== true
      || notice.ordinary_price_eligible !== false
      || notice.catalog_id !== product.catalog_id || notice.page !== product.page
      || typeof notice.source_price_mdl !== "number"
      || !Number.isFinite(notice.source_price_mdl) || notice.source_price_mdl <= 0) return null;
  return { current: notice.source_price_mdl, old: null, discount: null, conditional: true };
}

export function conditionalPriceText(product: Product, locale: AppLocale): string {
  // This only shortens an existing condition for compact cards. It never
  // establishes a price or changes eligibility; the full note stays available.
  const note = localizedNote(product, locale);
  const card = /Kaufland Card|по карте|с (?:цифровой )?картой|cu cardul|з (?:цифровою )?карткою/iu.test(note)
    && !/без карты|без картки|fără card/iu.test(note);
  const coupon = /купон|cupon/iu.test(note);
  const from = /цена от\s+\d|ціна від\s+\d|preț de la\s+\d/iu.test(note);
  const labels = {
    cardCoupon: { ru: "С купоном и картой", ro: "Cu cupon și card", uk: "З купоном і карткою" },
    card: { ru: "С картой", ro: "Cu cardul", uk: "З карткою" },
    coupon: { ru: "С купоном", ro: "Cu cupon", uk: "З купоном" },
    from: { ru: "Цена от", ro: "Preț de la", uk: "Ціна від" },
    other: { ru: "По условиям акции", ro: "Conform condițiilor promoției", uk: "За умовами акції" },
  };
  return labels[from ? "from" : card && coupon ? "cardCoupon" : card ? "card" : coupon ? "coupon" : "other"][locale];
}

function localizedNote(product: Product, locale: AppLocale): string {
  return {
    ru: product.note_ru || product.note_ro || "",
    ro: product.note_ro ?? "",
    uk: product.note_uk || product.note_ru || product.note_ro || "",
  }[locale];
}

/** Keep the actual condition, without repeating the price already shown above it. */
export function productDisplayNote(product: Product, locale: AppLocale): string {
  const note = localizedNote(product, locale);
  const price = productDisplayPrice(product);
  if (!price?.conditional) return note;
  let result = note;
  for (const [prefix, priceLabel] of [
    ["Смотрите условия в каталоге. ", "Указанная в каталоге цена"],
    ["Consultați condițiile din catalog. ", "Prețul indicat în catalog"],
    ["Дивіться умови в каталозі. ", "Ціна, зазначена в каталозі"],
  ] as const) {
    if (result.startsWith(prefix)) result = result.slice(prefix.length);
    result = result.replace(`${priceLabel}: ${price.current.toFixed(2)} MDL. `, "");
  }
  return result.trim();
}
